import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import { createStaffContact, updateStaffContact, getStaffById } from "../../services/StaffService";

const StaffContactForm = () => {
  const navigate = useNavigate();
  const { staffId, contactId } = useParams();
  const isEditMode = !!contactId;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  const [formData, setFormData] = useState({
    contact_type: "personal",
    email: "",
    phone_number: "",
    relation: ""
  });

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    if (isEditMode) {
      fetchContactData();
    }
  }, [staffId, contactId]);

  const fetchContactData = async () => {
    try {
      setLoading(true);
      const res = await getStaffById(staffId);
      const staff = res.data.data || res.data;
      const contact = staff.StaffContacts?.find(cont => cont.id === parseInt(contactId));
      
      if (contact) {
        setFormData({
          contact_type: contact.contact_type || "personal",
          email: contact.email || "",
          phone_number: contact.phone_number || "",
          relation: contact.relation || ""
        });
      } else {
        notifyError("Contact not found");
        navigate(`/staff/staffDetails/${staffId}`);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Contact fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load contact data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const isOnlyDigits = (str) => {
    const trimmed = (str || '').trim();
    if (!trimmed) return true;
    return /^\d+$/.test(trimmed);
  };

  const validate = () => {
    const errs = {};
    // Email validation if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = "Please enter a valid email address";
    }
    if (formData.phone_number.trim() && !isOnlyDigits(formData.phone_number)) {
      errs.phone_number = "Phone number must contain only numbers.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      notifyError("Please correct the errors in the form");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      if (isEditMode) {
        await updateStaffContact(staffId, contactId, formData);
        notifySuccess("Contact updated successfully");
      } else {
        await createStaffContact(staffId, formData);
        notifySuccess("Contact added successfully");
      }
      setTimeout(() => navigate(`/staff/staffDetails/${staffId}`), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} contact`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <h4 className="mb-4">{isEditMode ? "Edit Contact" : "Add Contact"}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6 mb-3">
              <label>Contact Type*</label>
              <select
                name="contact_type"
                value={formData.contact_type}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="personal">Personal</option>
                <option value="emergency contact">Emergency Contact</option>
              </select>
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                placeholder="Enter email address"
              />
              {errors.email && <div className="text-danger small">{errors.email}</div>}
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Phone Number</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="phone_number"
                value={formData.phone_number}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d+$/.test(v)) {
                    setFormData((prev) => ({ ...prev, phone_number: v }));
                    if (errors.phone_number) setErrors((prev) => { const n = { ...prev }; delete n.phone_number; return n; });
                  }
                }}
                className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`}
                placeholder="Enter phone number (numbers only)"
              />
              {errors.phone_number && <div className="text-danger small">{errors.phone_number}</div>}
            </div>

            <div className="col-12 col-md-6 mb-3">
              <label>Relation</label>
              <input
                name="relation"
                value={formData.relation}
                onChange={handleChange}
                className="form-control"
                placeholder="e.g., Father, Mother, Spouse, Brother"
              />
            </div>
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2 w-100">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
                disabled={isSubmitting}
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn user-form-btn sw-btn-next"
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default StaffContactForm;
